import HttpClient, { IResponseModel } from "../HttpClient";
import {
  ICognitoUser,
  ICognitoUserWithOrgData,
  IFilesTags,
  IFilesTagsAction,
  IInvoice,
  IOrganization,
  ISingleOrganizationAndUsers,
} from "@tedivo/tvd-api-models";

export type TCognitoRemediateAction =
  | "force-confirm"
  | "confirm"
  | "enable"
  | "disable";

const urlOrganizations = "organizations";

const OrganizationsService = {
  adminGetAllOrganizations: async (): Promise<
    IResponseModel<IOrganization[]>
  > => {
    const client = new HttpClient();
    return await client.request<IOrganization[]>({
      method: "GET",
      url: urlOrganizations,
    });
  },

  adminGetAllUsers: async (): Promise<
    IResponseModel<ICognitoUserWithLastLoginDate[]>
  > => {
    const client = new HttpClient();
    return await client.request<ICognitoUserWithLastLoginDate[]>({
      method: "GET",
      url: "admin-users",
    });
  },

  getSingleOrganizationAndUsers: async (
    orgId: string,
  ): Promise<IResponseModel<ISingleOrganizationAndUsers>> => {
    const client = new HttpClient();
    return await client.request<ISingleOrganizationAndUsers>({
      method: "GET",
      url: `${urlOrganizations}/${orgId}/users`,
    });
  },

  getOrganizationName: async (
    orgId: string,
  ): Promise<IResponseModel<{ name: string }>> => {
    const client = new HttpClient();
    return await client.request<{ name: string }>({
      method: "GET",
      url: `organizationName/${orgId}`,
    });
  },

  updateFileTags: async (
    data: IFilesTagsAction[],
  ): Promise<IResponseModel<IFilesTags>> => {
    const client = new HttpClient();
    return await client.request<IFilesTags>({
      method: "PUT",
      url: "organizations/updateTags",
      data,
    });
  },

  getFileTags: async (): Promise<IResponseModel<IFilesTags>> => {
    const client = new HttpClient();
    return await client.request<IFilesTags>({
      method: "GET",
      url: "organizations/getTags",
    });
  },

  getInvoicesOfOrg: async (
    orgId: string,
  ): Promise<IResponseModel<IInvoice[]>> => {
    const client = new HttpClient();
    return await client.request<IInvoice[]>({
      method: "GET",
      url: `organizations/invoice/${orgId}`,
    });
  },

  getInvoice: async (
    id: string,
    orgId: string,
  ): Promise<IResponseModel<IInvoice>> => {
    const client = new HttpClient();
    return await client.request<IInvoice>({
      method: "GET",
      url: `organizations/invoice/${orgId}/${id}`,
    });
  },

  deleteInvoice: async (
    id: string,
    orgId?: string,
  ): Promise<IResponseModel<unknown>> => {
    if (!orgId) {
      throw new Error("Organization ID is required");
    }

    const client = new HttpClient();
    return await client.request<unknown>({
      method: "DELETE",
      url: `organizations/invoice/${orgId}/${id}`,
      data: undefined,
    });
  },

  createUpdateInvoice: async (
    data: IInvoice,
  ): Promise<IResponseModel<ICreateOutput>> => {
    const client = new HttpClient();
    return await client.request<ICreateOutput>({
      method: "PUT",
      url: `organizations/invoice/${data.organizationId}`,
      data,
    });
  },

  adminCreateOrganization: async (
    data: Omit<IOrganization, "organizationId">,
  ): Promise<IResponseModel<ICreateOutput>> => {
    const client = new HttpClient();
    return await client.request<ICreateOutput>({
      method: "PUT",
      url: urlOrganizations,
      data,
    });
  },

  adminUpdateOrganization: async (
    data: IOrganization,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "PUT",
      url: urlOrganizations,
      data,
    });
  },

  adminDeleteOrganization: async (
    id: string,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "DELETE",
      url: `${urlOrganizations}/${id}`,
      data: undefined,
    });
  },

  adminCreateOrganizationUser: async (
    orgId: string,
    data: Omit<ICognitoUserWithOrgData, "sub">,
  ): Promise<IResponseModel<ICreateOutput | ICreateOutputError>> => {
    const client = new HttpClient();
    return await client.request<ICreateOutput | ICreateOutputError>({
      method: "PUT",
      url: `${urlOrganizations}/${orgId}/users`,
      data,
    });
  },

  adminUpdateOrganizationUser: async (
    orgId: string,
    data: ICognitoUserWithOrgData,
  ): Promise<IResponseModel<ICreateOutput | ICreateOutputError>> => {
    const client = new HttpClient();
    return await client.request<ICreateOutput | ICreateOutputError>({
      method: "PUT",
      url: `${urlOrganizations}/${orgId}/users`,
      data,
    });
  },

  adminDeleteOrganizationUser: async (
    orgId: string,
    userId: string,
    username = userId,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "DELETE",
      url: `${urlOrganizations}/${orgId}/users/${userId}`,
      data: { username },
    });
  },

  adminRemediateOrganizationUser: async (
    orgId: string,
    userId: string,
    action: TCognitoRemediateAction,
  ): Promise<IResponseModel<{ action: string }>> => {
    const client = new HttpClient();
    return await client.request<{ action: string }>({
      method: "POST",
      url: `${urlOrganizations}/${orgId}/users/${userId}/remediate`,
      data: { action },
    });
  },
};

export default OrganizationsService;

type ICreateOutput = { id: string; merged?: boolean };
type ICreateOutputError = { id?: never; code: string; message: string };
export type ICognitoUserWithLastLoginDate = ICognitoUser & {
  lastLoginDate?: string;
};
